import { t } from '../../lib/i18n';

/** The minimum rejection-reason length the API's reject endpoint enforces. */
export const MIN_REJECT_REASON_LENGTH = 10;

interface RejectModalProps {
  reason: string;
  isBusy: boolean;
  onReasonChange: (reason: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Rejection-reason dialog.
 *
 * The artboard has no modal — desktop captures the note in the detail panel's
 * textarea. It survives because the reason is MANDATORY (the API rejects
 * anything under ten characters) and the mobile card has nowhere to type one:
 * the phone's «Відхилити» has to ask. The desktop textarea pre-fills it, so the
 * moderator who already wrote a note confirms rather than retypes.
 */
export function RejectModal({
  reason,
  isBusy,
  onReasonChange,
  onCancel,
  onConfirm,
}: RejectModalProps) {
  const tooShort = reason.trim().length < MIN_REJECT_REASON_LENGTH;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      className="fixed inset-0 z-50 flex items-center justify-center bg-cf-ink/50 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-cf-lg border border-cf-line bg-cf-surface p-6"
      >
        <h3 className="text-[17px] font-semibold tracking-[-0.02em]">
          {t('verification.admin.docs.rejectModal.title')}
        </h3>
        <p className="mt-2 text-[13px] leading-[1.5] text-cf-ink-subtle">
          {t('verification.admin.docs.rejectModal.body')}
        </p>
        <textarea
          value={reason}
          rows={4}
          onChange={(event) => onReasonChange(event.target.value)}
          placeholder={t('verification.admin.docs.rejectModal.placeholder')}
          className="mt-3 w-full resize-y rounded-cf-md border border-cf-line px-3 py-2.5 text-[13px]"
        />
        <p className="mt-1 text-right font-mono text-[11px] text-cf-ink-subtle">
          {reason.trim().length}/{MIN_REJECT_REASON_LENGTH}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink"
          >
            {t('verification.admin.docs.rejectModal.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={tooShort || isBusy}
            className="h-10 rounded-cf-md bg-cf-danger px-3.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {t('verification.admin.docs.rejectModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
